import React, { useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Video from '../../../components/AppVideo';


const MediaTab = ({ formData, onChange }) => {
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    const newImages = files?.map((file, index) => ({
      id: Date.now() + index,
      url: URL.createObjectURL(file),
      alt: `صورة العقار ${(formData?.images?.length || 0) + index + 1}`,
      name: file?.name,
      size: file?.size,
      isPrimary: formData?.images?.length === 0 && index === 0
    }));

    onChange('images', [...(formData?.images || []), ...newImages]);
  };

  const handleVideoSelect = (e) => {
    const files = Array.from(e?.target?.files);
    const newVideos = files?.map((file, index) => ({
      id: Date.now() + index,
      url: URL.createObjectURL(file),
      name: file?.name,
      size: file?.size,
      type: file?.type,
      thumbnail: null
    }));

    onChange('videos', [...(formData?.videos || []), ...newVideos]);
  };

  const handleRemoveImage = (imageId) => {
    const updatedImages = formData?.images?.filter(img => img?.id !== imageId);
    onChange('images', updatedImages);
  };

  const handleRemoveVideo = (videoId) => {
    const updatedVideos = formData?.videos?.filter(vid => vid?.id !== videoId);
    onChange('videos', updatedVideos);
  };

  const handleSetPrimary = (imageId) => {
    const updatedImages = formData?.images?.map(img => ({
      ...img,
      isPrimary: img?.id === imageId
    }));
    onChange('images', updatedImages);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    const files = Array.from(e?.dataTransfer?.files);
    const imageFiles = files?.filter(file => file?.type?.startsWith('image/'));
    const videoFiles = files?.filter(file => file?.type?.startsWith('video/'));
    
    if (imageFiles?.length > 0) {
      const newImages = imageFiles?.map((file, index) => ({
        id: Date.now() + index,
        url: URL.createObjectURL(file),
        alt: `صورة العقار ${(formData?.images?.length || 0) + index + 1}`,
        name: file?.name,
        size: file?.size,
        isPrimary: formData?.images?.length === 0 && index === 0
      }));

      onChange('images', [...(formData?.images || []), ...newImages]);
    }

    if (videoFiles?.length > 0) {
      const newVideos = videoFiles?.map((file, index) => ({
        id: Date.now() + index + 1000,
        url: URL.createObjectURL(file),
        name: file?.name,
        size: file?.size,
        type: file?.type,
        thumbnail: null
      }));

      onChange('videos', [...(formData?.videos || []), ...newVideos]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' بايت';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' كيلوبايت';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' ميجابايت';
  };

  return (
    <div className="space-y-4">
      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className="border-2 border-dashed border-border rounded-lg p-6 text-center bg-muted/30 hover:bg-muted/50 transition-all duration-250"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        <input
          ref={videoInputRef}
          type="file"
          multiple
          accept="video/*"
          onChange={handleVideoSelect}
          className="hidden"
        />

        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="Upload" size={24} className="text-primary" />
          </div>

          <div>
            <h3 className="text-base font-heading font-semibold text-foreground mb-1">
              رفع صور وفيديوهات العقار
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              اسحب وأفلت الملفات هنا أو انقر للاختيار
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => fileInputRef?.current?.click()}
              iconName="ImagePlus"
              iconPosition="left"
            >
              اختيار الصور
            </Button>
            <Button
              variant="outline"
              onClick={() => videoInputRef?.current?.click()}
              iconName="Video"
              iconPosition="left"
            >
              اختيار فيديو
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            الصور: JPG, PNG, WebP (حد أقصى 5 ميجابايت) | الفيديو: MP4, MOV, WebM (حد أقصى 50 ميجابايت)
          </p>
        </div>
      </div>

      {formData?.videos && formData?.videos?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
              الفيديوهات المرفوعة ({formData?.videos?.length})
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange('videos', [])}
              iconName="Trash2"
              iconPosition="left"
            >
              حذف الكل
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {formData?.videos?.map((video) => (
              <div
                key={video?.id}
                className="relative group bg-card border border-border rounded-lg overflow-hidden hover:shadow-warm transition-all duration-250"
              >
                <div className="aspect-video bg-muted/50 flex items-center justify-center">
                  <Video
                    src={video?.url}
                    controls
                    loading="lazy"
                    preload="metadata"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded-full font-medium flex items-center gap-1">
                  <Icon name="Video" size={12} />
                  فيديو
                </div>

                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-250">
                  <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center justify-end">
                    <button
                      onClick={() => handleRemoveVideo(video?.id)}
                      className="p-1.5 bg-error/90 hover:bg-error text-white rounded-lg transition-all duration-250"
                      aria-label="حذف الفيديو"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </div>

                <div className="p-3 border-t border-border">
                  <p className="text-xs text-foreground truncate mb-1">{video?.name}</p>
                  <p className="text-xs text-muted-foreground font-data">{formatFileSize(video?.size)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {formData?.images && formData?.images?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
              الصور المرفوعة ({formData?.images?.length})
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange('images', [])}
              iconName="Trash2"
              iconPosition="left"
            >
              حذف الكل
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {formData?.images?.map((image) => (
              <div
                key={image?.id}
                className="relative group bg-card border border-border rounded-lg overflow-hidden hover:shadow-warm transition-all duration-250"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <Image
                    src={image?.url}
                    alt={image?.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-250"
                  />
                </div>

                {image?.isPrimary && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full font-medium">
                    الصورة الرئيسية
                  </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-250">
                  <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center justify-between gap-2">
                    {!image?.isPrimary && (
                      <button
                        onClick={() => handleSetPrimary(image?.id)}
                        className="flex-1 px-3 py-1.5 bg-white/90 hover:bg-white text-gray-900 rounded-lg text-xs font-medium transition-all duration-250"
                      >
                        تعيين كرئيسية
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveImage(image?.id)}
                      className="p-1.5 bg-error/90 hover:bg-error text-white rounded-lg transition-all duration-250"
                      aria-label="حذف الصورة"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </div>

                <div className="p-3 border-t border-border">
                  <p className="text-xs text-foreground truncate mb-1">{image?.name}</p>
                  <p className="text-xs text-muted-foreground font-data">{formatFileSize(image?.size)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-muted/50 p-4 md:p-6 rounded-lg border border-border">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-2">نصائح لرفع الوسائط:</p>
            <ul className="list-disc list-inside space-y-1 mr-4">
              <li>استخدم صوراً عالية الجودة وواضحة</li>
              <li>التقط صوراً من زوايا مختلفة للعقار</li>
              <li>قم بتصوير فيديو قصير (1-3 دقائق) يعرض العقار</li>
              <li>تأكد من إضاءة جيدة في الصور والفيديو</li>
              <li>قم بتعيين أفضل صورة كصورة رئيسية</li>
              <li>يُنصح برفع 8-12 صورة وفيديو واحد على الأقل</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaTab;